import React, { useEffect, useRef, useState, useCallback } from "react";
import { Script } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { scriptLoader } from "util/scriptLoader";
import { useStore, StoreTypes } from "context";
import { parseChineseOnly } from "util/zhuyin";
import * as types from "constants/actionTypes";
import { PAGE_SIZE } from "./utils/constants";
import { splitIntoPagesString } from "./utils/pageSplitter";
import TextStructureHandler from "./TextStructureHandler";
import ContentStructureHandler from "./ContentStructureHandler";
import { EventBusType, EventBus } from 'events/EventBus';
import { CanvasEvent } from 'events/EventTypes';
import { LayoutType } from "components/ExtendedContent/ExtendedContentContainer/layout";
import { SplitType } from "constants/splitType";
import { adjustImagesToFitPage } from "./TextStructureHandler/display";

const StaticHtmlComponent = ({ html, extendedResourceStyle }) => {
  const [{ bookId, books }] = useStore(StoreTypes.books);
  const [{ shouldSplited, splitType, totalPages, currentPage }, extendedContentDispatch] = useStore(StoreTypes.extendedContent);
  const book = books.find((info) => info.bookId === bookId);
  const highGradeCheck = book.display_name.includes("國中國文");
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const containerHeightRef = useRef(0);
  const isAdjustingRef = useRef(false);
  const [pagedHtml, setPagedHtml] = useState(null);
  const prevPageRef = useRef(currentPage); // 追蹤上一個頁面索引

  // 更新顯示的頁面 (參考 SynopsisWindow 的做法)
  const updateDisplayPage = useCallback((pageIndex) => {
    if (!contentRef.current) return;
    
    // 判斷 DOM 結構類型並選擇對應的頁面元素選擇器
    const textElements = contentRef.current.querySelectorAll('.text');
    const contentElements = contentRef.current.querySelectorAll('div.content');
    
    if (textElements.length > 0) {
      // .text 結構：每個 .text 元素代表一頁
      textElements.forEach((pageEl, index) => {
        if (index === pageIndex) {
          pageEl.style.visibility = 'visible';
          pageEl.style.position = 'relative';
        } else {
          pageEl.style.visibility = 'hidden';
          pageEl.style.position = 'absolute';
        }
      });
    } else if (contentElements.length > 0) {
      // div.content 結構：每個 .content 元素代表一頁
      contentElements.forEach((pageEl, index) => {
        if (index === pageIndex) {
          pageEl.style.visibility = 'visible';
          pageEl.style.position = 'relative';
        } else {
          pageEl.style.visibility = 'hidden';
          pageEl.style.position = 'absolute';
        }
      });
    }
  }, []);

  // 重置內容縮放到預設大小
  const resetContentScale = useCallback(() => {
    if (!contentRef.current) return;
    const content = contentRef.current;
    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.transformOrigin = '';
    content.style.height = '';
    content.style.width = '100%';
  }, []);

  // 調整內容縮放，使內容能顯示在可見範圍內
  const adjustContentScale = useCallback(() => {
    if (!containerRef.current || !contentRef.current) return;
    if (isAdjustingRef.current) return;

    const container = containerRef.current;
    const content = contentRef.current;
    const containerHeight = container.clientHeight;
    if (containerHeight <= 0) return;
    containerHeightRef.current = containerHeight;

    isAdjustingRef.current = true;

    content.style.fontSize = '';
    content.style.transform = 'none';
    content.style.transformOrigin = '';
    content.style.height = '';
    content.style.width = '100%';

    // .title 用 10vmin 等絕對值，不繼承父層 fontSize，需單獨處理
    const fixedEls = content.querySelectorAll('.title');
    fixedEls.forEach(el => { el.style.fontSize = ''; });

    void container.offsetHeight;

    // 記錄 .title 原始計算字體大小
    const fixedBaseSizes = [];
    fixedEls.forEach(el => {
      fixedBaseSizes.push(parseFloat(window.getComputedStyle(el).fontSize));
    });

    let contentHeight = content.scrollHeight;

    if (contentHeight > containerHeight && contentHeight > 0) {
      // 取得原始字體大小
      const computedStyle = window.getComputedStyle(content);
      const originalFontSize = parseFloat(computedStyle.fontSize);

      let minFontSize = originalFontSize * 0.3;
      let maxFontSize = originalFontSize;
      const tolerance = 0;
      const maxIterations = 20;

      let bestFontSize = minFontSize;
      let iteration = 0;

      while (maxFontSize - minFontSize > 0.5 && iteration < maxIterations) {
        const midFontSize = (minFontSize + maxFontSize) / 2;
        const scale = midFontSize / originalFontSize;

        content.style.fontSize = `${midFontSize}px`;
        fixedEls.forEach((el, i) => {
          el.style.fontSize = `${fixedBaseSizes[i] * scale}px`;
        });

        void content.offsetHeight;
        
        // 測量調整後的實際高度
        const actualHeight = content.scrollHeight;

        if (actualHeight <= containerHeight + tolerance) {
          // 高度合適，記錄這個字體大小，嘗試更大的值
          bestFontSize = midFontSize;
          minFontSize = midFontSize;
        } else {
          // 高度太大，需要更小的字體
          maxFontSize = midFontSize;
        }

        iteration++;
      }

      // 2% 安全邊距，避免邊界情況溢出
      const finalScale = (bestFontSize * 0.98) / originalFontSize;
      content.style.fontSize = `${bestFontSize * 0.98}px`;
      fixedEls.forEach((el, i) => {
        el.style.fontSize = `${fixedBaseSizes[i] * finalScale}px`;
      });

    } else {
      // 內容未超過容器，保持預設字體大小
      content.style.fontSize = '';
      fixedEls.forEach(el => { el.style.fontSize = ''; });
    }

    content.style.visibility = '';
    isAdjustingRef.current = false;
  }, []);
  
  // 筆記標記相關的狀態
  // const [{ currentPageIndex }, setState] = useSetState({
  //   currentPageIndex: 0
  // });

  // 移除 MathML annotation，避免 MTEF 編碼在舊版 Chromium 顯示為亂碼
  const stripMathAnnotations = (html) => {
    if (!html || typeof html !== 'string') return html;
    return html
      .replace(/<annotation-xml[^>]*>[\s\S]*?<\/annotation-xml>/gi, '')
      .replace(/<annotation[^>]*>[\s\S]*?<\/annotation>/gi, '');
  };

  // 國中國文 (highGradeCheck) 需移除注音標籤以縮小行距 (hruby line-height:1.8 → 預設)。
  // 各分支先取出單頁字串再傳入，確保 NO_SPLIT / MANUAL_SPLIT / AUTO_SPLIT 各模式行為都與 1.27.0 一致。
  const parseHighGrade = (value) =>
    highGradeCheck && typeof value === "string"
      ? parseChineseOnly(value, "pirls")
      : value;

  // 將腳本載入移到分頁完成後
  useEffect(() => {
    if (pagedHtml && contentRef.current && html) {
      // 等待 DOM 完全渲染後再載入腳本
      const timer = setTimeout(() => {
        scriptLoader(Script[extendedResourceStyle]);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [pagedHtml, extendedResourceStyle, html]);

  useEffect(() => {
    if (!shouldSplited) return;
    if (pagedHtml && currentPage >= 0) {
      // 如果頁面真的改變了，保存舊頁面的畫記
      if (prevPageRef.current !== currentPage && prevPageRef.current >= 0) {
        EventBus.emit({
          eventBusType: EventBusType.ExtendedContent,
          event: CanvasEvent.SplitedPageSaveCanvasEvent,
          payload: { 
            currentSplitedPageIndex: prevPageRef.current
          }
        });
      }
      
      // 載入新頁面的畫記
      EventBus.emit({
        eventBusType: EventBusType.ExtendedContent,
        event: CanvasEvent.SplitedPageImportJSONEvent,
        payload: { 
          currentSplitedPageIndex: currentPage
        }
      });
      
      // 更新 ref
      prevPageRef.current = currentPage;
    }
  }, [currentPage, pagedHtml, shouldSplited]);

  // 響應 currentPage 變化，切換顯示內容 (參考 SynopsisWindow 的做法)
  useEffect(() => {
    // NO_SPLIT 或未啟用分頁時不需要處理
    if (!shouldSplited || splitType === SplitType.NO_SPLIT) return;
    
    // MANUAL_SPLIT 模式：html 是物件格式 { '1': html1, '2': html2, ... }，直接更換 pagedHtml 內容
    if (splitType === SplitType.MANUAL_SPLIT) {
      const page = (currentPage + 1).toString();
      // 確保 html 是物件且有對應頁面
      if (html && typeof html === 'object' && html[page]) {
        setPagedHtml(stripMathAnnotations(parseHighGrade(html[page])));
      }
      return;
    }

    // AUTO_SPLIT 模式：html 是字串，分頁後透過 visibility 切換顯示
    if (splitType === SplitType.AUTO_SPLIT && pagedHtml && contentRef.current && totalPages > 0 && currentPage >= 0) {
      updateDisplayPage(currentPage);
    }
  }, [html, currentPage, pagedHtml, totalPages, shouldSplited, splitType, highGradeCheck, updateDisplayPage]);

  // 分頁處理
  useEffect(() => {
    if (!html || !containerRef.current) return;

    // MANUAL_SPLIT：html 是 { '1': html1, ... } 物件，先取出單頁字串處理（比照 SynopsisWindow）
    if (shouldSplited && splitType === SplitType.MANUAL_SPLIT) {
      setPagedHtml(stripMathAnnotations(parseHighGrade(html['1'])));
      // 更新 store 中的總頁數
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages: Object.keys(html).length },
      });
      return;
    }

    // 到這裡 html 一定是字串，先移除注音一次供下方 NO_SPLIT / AUTO_SPLIT 共用
    const parsedHtml = parseHighGrade(html);

    // 3/12 #NBK-1408 splitType為undefined時導致畫面出現錯誤，為日後可追蹤此問題所以先註解掉
    // splitType 為 undefined 時，視為不切頁
    // if (splitType === SplitType.undefined) {
    //   setPagedHtml(html);
    //   extendedContentDispatch({ type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES, payload: { totalPages: 1 } });
    //   return;
    // }

    // 不分頁 / NO_SPLIT：單頁顯示
    if (!shouldSplited || splitType === SplitType.NO_SPLIT) {
      setPagedHtml(stripMathAnnotations(parsedHtml));
      // 更新 store 中的總頁數為 1
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages: 1 },
      });
      return;
    }

    // AUTO_SPLIT
    resetContentScale();

    const measureContainer = containerRef.current;
    const originalHtml = parsedHtml;

    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;

      const computedStyle = window.getComputedStyle(measureContainer);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 
                        parseFloat(computedStyle.fontSize) * 1.5;
      
      const { htmlString, totalPages } = splitIntoPagesString(
        originalHtml, 
        lineHeight, 
        PAGE_SIZE,
        measureContainer,
        extendedResourceStyle
      );
      
      if (cancelled) return;
      setPagedHtml(htmlString);
      extendedContentDispatch({
        type: types.SET_EXTENDED_CONTENT_TOTAL_PAGES,
        payload: { totalPages },
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [html, highGradeCheck, extendedContentDispatch, extendedResourceStyle, shouldSplited, splitType, resetContentScale]);

  // 當頁面切換、內容變化時調整縮放
  // 1. 穩定性檢測：連續兩個 frame 取得相同 clientHeight 才執行初始縮放
  // 2. MutationObserver：MathJax、scriptLoader 等異步改變 DOM 導致溢出時重新縮放
  // 3. ResizeObserver：容器尺寸變化（父層動畫/resize）時重新縮放
  useEffect(() => {
    if (!pagedHtml || !containerRef.current || !contentRef.current || !shouldSplited) return;

    let cancelled = false;
    let debounceId = null;
    let recheckId = null;
    const content = contentRef.current;

    // 隱藏內容直到縮放完成，防止未縮放內容閃爍
    content.style.visibility = 'hidden';

    // 穩定性檢測：等待佈局穩定後執行初始縮放
    let prevHeight = -1;
    let retries = 0;
    const MAX_RETRIES = 30;

    const waitForStableLayout = () => {
      if (cancelled) return;
      const h = containerRef.current ? containerRef.current.clientHeight : 0;
      if (h > 0 && h === prevHeight) {
        adjustContentScale();
        // 延遲覆核：首次開啟時父層的外部 CSS（<link> 標籤）可能尚未載入，
        // 導致初始測量的容器尺寸不正確。延遲再檢查一次以補捉 CSS 載入後的版面變化。
        recheckId = setTimeout(() => {
          if (cancelled || !contentRef.current || !containerRef.current) return;
          if (contentRef.current.scrollHeight > containerRef.current.clientHeight) {
            adjustContentScale();
          }
        }, 500);
      } else if (retries < MAX_RETRIES) {
        prevHeight = h;
        retries++;
        requestAnimationFrame(waitForStableLayout);
      } else {
        if (contentRef.current) contentRef.current.style.visibility = '';
      }
    };
    requestAnimationFrame(waitForStableLayout);

    // MutationObserver：只在內容實際溢出容器時才重新縮放
    const mutationObserver = new MutationObserver(() => {
      if (cancelled || isAdjustingRef.current) return;
      if (!contentRef.current || !containerRef.current) return;
      if (contentRef.current.scrollHeight <= containerRef.current.clientHeight) return;
      clearTimeout(debounceId);
      debounceId = setTimeout(() => {
        if (cancelled) return;
        adjustContentScale();
      }, 80);
    });
    mutationObserver.observe(content, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      cancelled = true;
      clearTimeout(debounceId);
      clearTimeout(recheckId);
      mutationObserver.disconnect();
      if (contentRef.current) contentRef.current.style.visibility = '';
    };
  }, [pagedHtml, currentPage, shouldSplited, splitType]);

  // 限制 .text 內 img 的最大高度，避免圖片超出容器
  useEffect(() => {
    if (!pagedHtml || !contentRef.current || !shouldSplited) return;
    const rafId = requestAnimationFrame(() => {
      const maxH = containerHeightRef.current > 0
        ? `${containerHeightRef.current}px`
        : '80vh';
      const textImgs = contentRef.current.querySelectorAll('.text img');
      textImgs.forEach(img => {
        img.style.maxHeight = maxH;
        img.style.objectFit = 'contain';
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [pagedHtml, currentPage, shouldSplited]);

  // 確保即使不需要分頁，也要處理圖片超出畫面高度的情況
  useEffect(() => {
    if (!pagedHtml || !splitType || !contentRef.current || !containerRef.current) return;

    const isTextStructureType = extendedResourceStyle === LayoutType.PlainText || 
                                extendedResourceStyle === LayoutType.Topic_108_2;
    const shouldHandleImageResize = isTextStructureType && shouldSplited && splitType === SplitType.NO_SPLIT;

    if (!shouldHandleImageResize) return;

    const rafId = requestAnimationFrame(() => adjustImagesToFitPage(contentRef, 0));
    return () => cancelAnimationFrame(rafId);
  }, [pagedHtml, contentRef, containerRef, extendedResourceStyle, shouldSplited, splitType]);

  useEffect(() => {
    // 非 split 模式由 ExtendedContentContainer 的 <MathJax.Text> 負責 typeset；
    // 此處自管 typeset 只在 split 模式跑，避免同一份 DOM 被兩個 owner 重排（公式顯示兩次）。
    if (!shouldSplited) return;
    if (pagedHtml && contentRef.current) {
      if (window.MathJax && window.MathJax.Hub) {
        window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub, contentRef.current]);
      }
    }
  }, [pagedHtml, shouldSplited]);
  

  return (
    <div ref={containerRef}>
      {pagedHtml && (
        <>
          <div
            ref={contentRef}
            style={{ height: '100%' }}
            dangerouslySetInnerHTML={{
              __html: pagedHtml,
            }}
          />
          {shouldSplited && splitType === SplitType.AUTO_SPLIT && (extendedResourceStyle === LayoutType.PlainText || extendedResourceStyle === LayoutType.Topic_108_2) && (
            <TextStructureHandler
              pagedHtml={pagedHtml}
              currentPage={currentPage}
              currentPageIndex={currentPage}
              contentRef={contentRef}
            />
          )}
          {shouldSplited && splitType === SplitType.AUTO_SPLIT && (extendedResourceStyle === LayoutType.Simple_108_2 || extendedResourceStyle === LayoutType.Fill_108_2) && (
            <ContentStructureHandler
              pagedHtml={pagedHtml}
              currentPage={currentPage}
              currentPageIndex={currentPage}
              contentRef={contentRef}
            />
          )}
        </>
      )}
    </div>
  );
};

export default StaticHtmlComponent;
